import { createContext, useContext, useReducer, ReactNode, FC, useEffect } from 'react';
import {
  PerformanceState,
  PerformanceAction,
  PerformanceActionType,
  PerformanceContextType,
  PerformanceReviewType,
  PerformanceFilters,
  BasePerformanceReview,
  PerformanceSummary,
} from './performance.interfaces';
import {
  useStudentPerformanceReviewsHook,
  useStudentPerformanceSummaryHook,
  useStaffPerformanceReviewsHook,
  useStaffPerformanceSummaryHook,
  useExternalPerformanceReviewsHook,
  useExternalPerformanceSummaryHook,
} from './hooks/usePerformanceReviewsHook';

// Initial state
const initialState: PerformanceState = {
  currentReviewType: PerformanceReviewType.STUDENTS,
  studentReviews: [],
  staffReviews: [],
  externalReviews: [],
  studentSummary: {
    totalReviews: 0,
    completedReviews: 0,
    pendingReviews: 0,
    overdueReviews: 0,
    averageRating: 0,
    ratingDistribution: {
      excellent: 0,
      good: 0,
      satisfactory: 0,
      needsImprovement: 0,
      unsatisfactory: 0,
    },
  },
  staffSummary: {
    totalReviews: 0,
    completedReviews: 0,
    pendingReviews: 0,
    overdueReviews: 0,
    averageRating: 0,
    ratingDistribution: {
      excellent: 0,
      good: 0,
      satisfactory: 0,
      needsImprovement: 0,
      unsatisfactory: 0,
    },
  },
  externalSummary: {
    totalReviews: 0,
    completedReviews: 0,
    pendingReviews: 0,
    overdueReviews: 0,
    averageRating: 0,
    ratingDistribution: {
      excellent: 0,
      good: 0,
      satisfactory: 0,
      needsImprovement: 0,
      unsatisfactory: 0,
    },
  },
  filters: {
    reviewType: PerformanceReviewType.STUDENTS,
    status: 'all',
    rating: 'all',
    reviewPeriod: 'all',
    searchQuery: '',
    dateRange: {
      startDate: '',
      endDate: '',
    },
  },
  isLoading: false,
  error: null,
  selectedReview: null,
};

// Reducer function
const performanceReducer = (
  state: PerformanceState,
  action: PerformanceAction
): PerformanceState => {
  switch (action.type) {
    case PerformanceActionType.SET_REVIEW_TYPE:
      return {
        ...state,
        currentReviewType: action.payload,
        filters: {
          ...state.filters,
          reviewType: action.payload,
        },
      };

    case PerformanceActionType.SET_STUDENT_REVIEWS:
      return { ...state, studentReviews: action.payload };

    case PerformanceActionType.SET_STAFF_REVIEWS:
      return { ...state, staffReviews: action.payload };

    case PerformanceActionType.SET_EXTERNAL_REVIEWS:
      return { ...state, externalReviews: action.payload };

    case PerformanceActionType.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case PerformanceActionType.SET_SEARCH_QUERY:
      return {
        ...state,
        filters: { ...state.filters, searchQuery: action.payload },
      };

    case PerformanceActionType.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case PerformanceActionType.SET_ERROR:
      return { ...state, error: action.payload };

    case PerformanceActionType.SET_SELECTED_REVIEW:
      return { ...state, selectedReview: action.payload };

    case PerformanceActionType.UPDATE_STUDENT_SUMMARY:
      return { ...state, studentSummary: action.payload };

    case PerformanceActionType.UPDATE_STAFF_SUMMARY:
      return { ...state, staffSummary: action.payload };

    case PerformanceActionType.UPDATE_EXTERNAL_SUMMARY:
      return { ...state, externalSummary: action.payload };

    case PerformanceActionType.RESET_FILTERS:
      return {
        ...state,
        filters: {
          ...initialState.filters,
          reviewType: state.currentReviewType,
        },
      };

    default:
      return state;
  }
};

// Create context
const PerformanceContext = createContext<PerformanceContextType | undefined>(
  undefined
);

// Provider component
interface PerformanceProviderProps {
  children: ReactNode;
}

export const PerformanceProvider: FC<PerformanceProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(performanceReducer, initialState);
  const { data: studentReviewsData = [] } = useStudentPerformanceReviewsHook();
  const { data: studentSummaryData } = useStudentPerformanceSummaryHook();
  const { data: staffReviewsData = [] } = useStaffPerformanceReviewsHook();
  const { data: staffSummaryData } = useStaffPerformanceSummaryHook();
  const { data: externalReviewsData = [] } = useExternalPerformanceReviewsHook();
  const { data: externalSummaryData } = useExternalPerformanceSummaryHook();

  // Action creators
  const setReviewType = (type: PerformanceReviewType) => {
    dispatch({ type: PerformanceActionType.SET_REVIEW_TYPE, payload: type });
  };

  const setFilters = (filters: Partial<PerformanceFilters>) => {
    dispatch({ type: PerformanceActionType.SET_FILTERS, payload: filters });
  };

  const setSearchQuery = (query: string) => {
    dispatch({ type: PerformanceActionType.SET_SEARCH_QUERY, payload: query });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: PerformanceActionType.SET_LOADING, payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: PerformanceActionType.SET_ERROR, payload: error });
  };

  const setSelectedReview = (review: BasePerformanceReview | null) => {
    dispatch({
      type: PerformanceActionType.SET_SELECTED_REVIEW,
      payload: review,
    });
  };

  const resetFilters = () => {
    dispatch({ type: PerformanceActionType.RESET_FILTERS });
  };

  // Sync hook data with reducer state
  useEffect(() => {
    if (studentReviewsData.length > 0) {
      dispatch({
        type: PerformanceActionType.SET_STUDENT_REVIEWS,
        payload: studentReviewsData as any,
      });
    }
  }, [studentReviewsData]);

  useEffect(() => {
    if (studentSummaryData) {
      dispatch({
        type: PerformanceActionType.UPDATE_STUDENT_SUMMARY,
        payload: studentSummaryData,
      });
    }
  }, [studentSummaryData]);

  useEffect(() => {
    if (staffReviewsData.length > 0) {
      dispatch({
        type: PerformanceActionType.SET_STAFF_REVIEWS,
        payload: staffReviewsData as any,
      });
    }
  }, [staffReviewsData]);

  useEffect(() => {
    if (staffSummaryData) {
      dispatch({
        type: PerformanceActionType.UPDATE_STAFF_SUMMARY,
        payload: staffSummaryData,
      });
    }
  }, [staffSummaryData]);

  useEffect(() => {
    if (externalReviewsData.length > 0) {
      dispatch({
        type: PerformanceActionType.SET_EXTERNAL_REVIEWS,
        payload: externalReviewsData as any,
      });
    }
  }, [externalReviewsData]);

  useEffect(() => {
    if (externalSummaryData) {
      dispatch({
        type: PerformanceActionType.UPDATE_EXTERNAL_SUMMARY,
        payload: externalSummaryData,
      });
    }
  }, [externalSummaryData]);

  // Data fetching methods
  const fetchStudentReviews = async (): Promise<void> => {
    try {
      setLoading(true);
      if (studentReviewsData.length > 0) {
        dispatch({
          type: PerformanceActionType.SET_STUDENT_REVIEWS,
          payload: studentReviewsData as any,
        });
      }
      if (studentSummaryData) {
        dispatch({
          type: PerformanceActionType.UPDATE_STUDENT_SUMMARY,
          payload: studentSummaryData,
        });
      }
    } catch (error) {
      setError('Failed to fetch student reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffReviews = async (): Promise<void> => {
    try {
      setLoading(true);
      if (staffReviewsData.length > 0) {
        dispatch({
          type: PerformanceActionType.SET_STAFF_REVIEWS,
          payload: staffReviewsData as any,
        });
      }
      if (staffSummaryData) {
        dispatch({
          type: PerformanceActionType.UPDATE_STAFF_SUMMARY,
          payload: staffSummaryData,
        });
      }
    } catch (error) {
      setError('Failed to fetch staff reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchExternalReviews = async (): Promise<void> => {
    try {
      setLoading(true);
      if (externalReviewsData.length > 0) {
        dispatch({
          type: PerformanceActionType.SET_EXTERNAL_REVIEWS,
          payload: externalReviewsData as any,
        });
      }
      if (externalSummaryData) {
        dispatch({
          type: PerformanceActionType.UPDATE_EXTERNAL_SUMMARY,
          payload: externalSummaryData,
        });
      }
    } catch (error) {
      setError('Failed to fetch external reviews');
    } finally {
      setLoading(false);
    }
  };

  // Utility methods
  const getFilteredReviews = (): BasePerformanceReview[] => {
    let reviews: BasePerformanceReview[] = [];

    switch (state.currentReviewType) {
      case PerformanceReviewType.STUDENTS:
        reviews = state.studentReviews;
        break;
      case PerformanceReviewType.STAFF:
        reviews = state.staffReviews;
        break;
      case PerformanceReviewType.EXTERNAL:
        reviews = state.externalReviews;
        break;
    }

    return reviews.filter((review) => {
      // Filter by status
      if (
        state.filters.status !== 'all' &&
        review.status !== state.filters.status
      ) {
        return false;
      }

      // Filter by rating
      if (
        state.filters.rating !== 'all' &&
        review.overallRating !== state.filters.rating
      ) {
        return false;
      }

      // Filter by search query
      if (state.filters.searchQuery) {
        const searchLower = state.filters.searchQuery.toLowerCase();
        return (
          review.name.toLowerCase().includes(searchLower) ||
          review.reviewer.toLowerCase().includes(searchLower) ||
          review.comments.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  };

  const getCurrentSummary = (): PerformanceSummary => {
    switch (state.currentReviewType) {
      case PerformanceReviewType.STUDENTS:
        return state.studentSummary;
      case PerformanceReviewType.STAFF:
        return state.staffSummary;
      case PerformanceReviewType.EXTERNAL:
        return state.externalSummary;
      default:
        return state.studentSummary;
    }
  };

  const exportReviews = async (format: 'pdf' | 'excel'): Promise<void> => {
    try {
      setLoading(true);
      // Simulate export process
      await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log(`Exporting ${state.currentReviewType} reviews as ${format}`);
      // TODO: Implement actual export logic
    } catch (error) {
      setError(`Failed to export reviews as ${format}`);
    } finally {
      setLoading(false);
    }
  };

  const contextValue: PerformanceContextType = {
    state,
    dispatch,
    setReviewType,
    setFilters,
    setSearchQuery,
    setLoading,
    setError,
    setSelectedReview,
    resetFilters,
    fetchStudentReviews,
    fetchStaffReviews,
    fetchExternalReviews,
    getFilteredReviews,
    getCurrentSummary,
    exportReviews,
  };

  return (
    <PerformanceContext.Provider value={contextValue}>
      {children}
    </PerformanceContext.Provider>
  );
};

// Custom hook to use the context
export const usePerformance = (): PerformanceContextType => {
  const context = useContext(PerformanceContext);
  if (context === undefined) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
};

export default PerformanceContext;
